import '../styles/PageGlobal.css';
import './BonDeComFournisseurDetailsEditPage.css';
import '../components/BCFAdressePicker.css';
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import BCFAdressePicker from '../components/BCFAdressePicker';

import { BCFService } from '../services/BCFService.js';
import { AdresseService } from '../services/AdresseService.js';

// 'horizontal' = les 2 adresses côte à côte, 'vertical' = l'une sous l'autre
const ADRESSE_LAYOUT = 'horizontal';

// Bloc d'affichage d'une adresse (champs l'un sous l'autre)
function AdresseCard({ title, adresse, adresseId, onChoose, disabled }) {
  return (
    <div className="bcf-adresse-card">
      <div className="bcf-adresse-card-header">
        <h3 className="bcf-adresse-card-title">{title}</h3>
        <button
          className="boncomfournisseur-btn boncomfournisseur-btn-blue"
          onClick={onChoose}
          disabled={disabled}
        >
          Choisir
        </button>
      </div>

      {adresse ? (
        <address className="bcf-adresse-lines">
          {adresse.type && adresse.type !== 'NON' && <span><b>{adresse.type}</b></span>}
          {adresse.adresse1 && <span>{adresse.adresse1}</span>}
          {adresse.adresse2 && <span>{adresse.adresse2}</span>}
          {(adresse.codePostal || adresse.ville) && (
            <span>{`${adresse.codePostal || ''} ${adresse.ville || ''}`.trim()}</span>
          )}
          {adresse.pays && <span>{adresse.pays}</span>}
        </address>
      ) : (
        <p className="bcf-adresse-lines bcf-adresse-empty">
          {adresseId ? `Adresse #${adresseId} introuvable` : 'Aucune adresse définie'}
        </p>
      )}
    </div>
  );
}

export default function BonDeComFournisseurDetailsEditPage() {
  const { bon_com_fournisseur_id } = useParams();
  const navigate = useNavigate();
  const bcfService = useMemo(() => new BCFService(), []);
  const adresseService = useMemo(() => new AdresseService(), []);

  const [data, setData] = useState(null);
  const [lines, setLines] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [adresseLivraisonId, setAdresseLivraisonId] = useState(null);
  const [adresseFacturationId, setAdresseFacturationId] = useState(null);
  const [livraisonAdresse, setLivraisonAdresse] = useState(null);
  const [facturationAdresse, setFacturationAdresse] = useState(null);
  const [showLivPicker, setShowLivPicker] = useState(false);
  const [showFacPicker, setShowFacPicker] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const bcfData = await bcfService.findById(bon_com_fournisseur_id);
        setData(bcfData);
        setLines(bcfData.lines || []);
        setAdresseLivraisonId(bcfData.adresseLivraisonId ?? null);
        setAdresseFacturationId(bcfData.adresseFacturationId ?? null);

        // ids -> adresses complètes (un échec ici ne doit pas casser la page)
        try {
          const all = (await adresseService.getAllAdresses()) || [];
          const byId = (id) => all.find(a => String(a.id) === String(id)) || null;
          setLivraisonAdresse(byId(bcfData.adresseLivraisonId));
          setFacturationAdresse(byId(bcfData.adresseFacturationId));
        } catch (e) {
          console.warn("Adresses non chargées", e);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [bon_com_fournisseur_id, bcfService, adresseService]);

  const handleChangeQty = (id, value) => {
    setLines(lines.map(l => l.id === id ? { ...l, quantity: Number(value) } : l));
  };

  const handleDetachLine = async (lineId) => {
    if (!window.confirm("Détacher cette ligne du bon de commande ? Elle retournera dans les lignes orphelines.")) return;
    try {
      await bcfService.detachLine(bon_com_fournisseur_id, lineId);
      setLines(lines.filter(l => l.id !== lineId));
    } catch (err) {
      alert("Erreur détachement : " + err.message);
    }
  };

  const handleSave = async () => {
    try {
      await bcfService.update(bon_com_fournisseur_id, {
        lines,
        adresseLivraisonId,
        adresseFacturationId
      });
      alert("Bon de commande sauvegardé avec succès !");
    } catch (err) {
      alert("Erreur sauvegarde : " + err.message);
    }
  };

  const handleValidate = async () => {
    if (!window.confirm("Valider et clôturer ce bon de commande ? Cette action est irréversible.")) return;
    try {
      await bcfService.close(bon_com_fournisseur_id);
      alert("Bon de commande validé et clôturé avec succès !");
      navigate("/bon_com_fournisseur_list");
    } catch (err) {
      alert("Erreur validation : " + err.message);
    }
  };

  if (loading) return <div className="devis-status devis-status-loading">Chargement…</div>;
  if (error) return <div className="devis-status devis-status-error">{error}</div>;
  if (!data) return <div>Aucune donnée trouvée.</div>;

  const isClosed = data.status === 'CLOSED';

  return (
    <div className="containerMain boncomfournisseur-edit-container">
      <div className="boncomfournisseur-header">
        <h1 className="boncomfournisseur-title">Bon de commande Fournisseur Détails/Edit</h1>
      </div>

      <div style={{ marginTop: "15px", padding: "15px", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
        <p><b>Référence:</b> {data.reference || `BCF-${data.id}`}</p>
        <p><b>Fournisseur ID:</b> {data.supplierId || 'N/A'}</p>
        <p><b>Statut:</b> <span style={{ color: isClosed ? 'red' : 'green', fontWeight: 'bold' }}>{data.status || 'DRAFT'}</span></p>
      </div>

      {/* ── Adresses ── */}
      <div className={`bcf-adresses bcf-adresses-${ADRESSE_LAYOUT}`}>
        <AdresseCard
          title="Adresse de livraison"
          adresse={livraisonAdresse}
          adresseId={adresseLivraisonId}
          onChoose={() => setShowLivPicker(true)}
          disabled={isClosed}
        />
        <AdresseCard
          title="Adresse de facturation"
          adresse={facturationAdresse}
          adresseId={adresseFacturationId}
          onChoose={() => setShowFacPicker(true)}
          disabled={isClosed}
        />
      </div>

      {showLivPicker && (
  <BCFAdressePicker
    title="Adresse de livraison"
    onClose={() => setShowLivPicker(false)}
    onSelect={(adr) => { setAdresseLivraisonId(adr.id); setLivraisonAdresse(adr); }}
  />
)}
{showFacPicker && (
  <BCFAdressePicker
    title="Adresse de facturation"
    onClose={() => setShowFacPicker(false)}
    onSelect={(adr) => { setAdresseFacturationId(adr.id); setFacturationAdresse(adr); }}
  />
)}

      <div className="boncomfournisseur-table-wrapper" style={{ marginTop: "20px" }}>
        <table className="boncomfournisseur-table">
          <thead>
            <tr>
              <th>Ref</th>
              <th>Ref. Fabricant</th>
              <th>Quantité</th>
              <th>Prix Unitaire</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>Aucune ligne dans ce bon.</td></tr>
            ) : (
              lines.map(line => (
                <tr key={line.id}>
                  <td>{line.reference || 'N/A'}</td>
                  <td>{line.refFabricant || 'N/A'}</td>
                  <td>
                    <input
                      type="number"
                      value={line.quantity || 0}
                      onChange={(e) => handleChangeQty(line.id, e.target.value)}
                      disabled={isClosed}
                      style={{ width: "80px", padding: "4px" }}
                    />
                  </td>
                  <td>{line.prixUnit || line.unitPrice || 0} €</td>
                  <td>
                    <button
                      className="boncomfournisseur-btn boncomfournisseur-btn-red"
                      onClick={() => handleDetachLine(line.id)}
                      disabled={isClosed}
                    >
                      Detach Ligne
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="boncomfournisseur-total" style={{ marginTop: "20px", fontSize: "1.2em", fontWeight: "bold" }}>
        TOTAL: {lines.reduce((sum, l) => sum + ((l.prixUnit || l.unitPrice || 0) * (l.quantity || 0)), 0).toFixed(2)} €
      </div>

      <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
        <button
          className="boncomfournisseur-btn boncomfournisseur-btn-success"
          onClick={handleSave}
          disabled={isClosed}
        >
          Save
        </button>
        <button
          className="boncomfournisseur-btn boncomfournisseur-btn-blue"
          onClick={handleValidate}
          disabled={isClosed || lines.length === 0}
        >
          Validate & Close
        </button>
        <button
          className="boncomfournisseur-btn boncomfournisseur-btn-grey"
          onClick={() => navigate("/bon_com_fournisseur_list")}
        >
          Annuler
        </button>
      </div>
    </div>
  );
}